import { hot } from 'react-hot-loader/root';
import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { shallowEqual, useDispatch, useSelector, useStore } from 'react-redux';
import PropTypes from 'prop-types';
import selectors from 'selectors';
import useCore from 'hooks/useCore';
import actions from 'actions';

import LogoBar from 'components/LogoBar';
import Accessibility from 'components/Accessibility';
import DocumentContainer from 'components/DocumentContainer';
import FilePickerHandler from 'components/FilePickerHandler';
import CopyTextHandler from 'components/CopyTextHandler';
import PrintHandler from 'components/PrintHandler';
import FontHandler from 'components/FontHandler';
import TextEditingPanel from 'components/TextEditingPanel';
import AudioPlaybackPopup from 'components/AudioPlaybackPopup';
import DocumentCropPopup from 'components/DocumentCropPopup';
import SnippingToolPopup from '../SnippingToolPopup';
import EmbeddedJSPopup from 'components/EmbeddedJSPopup';
import FormFieldIndicatorContainer from 'components/FormFieldIndicator';
import MultiTabEmptyPage from 'components/MultiTabEmptyPage';
import MultiViewer from 'components/MultiViewer';
import ComparePanel from 'components/MultiViewer/ComparePanel';
import CustomElement from 'components/CustomElement';
import Panel from 'components/Panel';
import LeftHeader from 'components/ModularComponents/LeftHeader';
import RightHeader from 'components/ModularComponents/RightHeader';
import BottomHeader from 'components/ModularComponents/BottomHeader';
import TopHeader from 'components/ModularComponents/TopHeader';
import FlyoutContainer from 'components/ModularComponents/FlyoutContainer';
import RibbonOverflowFlyout from 'components/ModularComponents/RibbonOverflowFlyout';
import StylePanelFlyout from 'components/ModularComponents/StylePanelFlyout';
import ProgressModal from 'components/ProgressModal';
import LazyLoadWrapper, { LazyLoadComponents } from 'components/LazyLoadWrapper';
import useOnTextSelected from 'hooks/useOnTextSelected';
import useOnContextMenuOpen from 'hooks/useOnContextMenuOpen';
import useOnAnnotationPopupOpen from 'hooks/useOnAnnotationPopupOpen';
import useOnAlignmentPopupOpen from 'hooks/useOnAlignmentPopupOpen';
import useOnFormFieldAnnotationAddedOrSelected from 'hooks/useOnFormFieldAnnotationAddedOrSelected';
import useOnMeasurementToolOrAnnotationSelected from 'hooks/useOnMeasurementToolOrAnnotationSelected';
import useOnCountMeasurementAnnotationSelected from 'hooks/useOnCountMeasurementAnnotationSelected';
import useOnInlineCommentPopupOpen from 'hooks/useOnInlineCommentPopupOpen';
import useOnRightClickAnnotation from 'hooks/useOnRightClickAnnotation';
import useOnAnnotationContentOverlayOpen from 'hooks/useOnAnnotationContentOverlayOpen';
import useOnLinkAnnotationPopupOpen from 'hooks/useOnLinkAnnotationPopupOpen';
import useOnAnnotationCreateSignatureToolMode from 'hooks/useOnAnnotationCreateSignatureToolMode';
import useOnAnnotationCreateRubberStampToolMode from 'hooks/useOnAnnotationCreateRubberStampToolMode';
import useOnRedactionAnnotationChanged from 'hooks/useOnRedactionAnnotationChanged';
import useOnHeaderFooterUpdate from 'src/hooks/useOnHeaderFooterUpdate';
import loadDocument from 'helpers/loadDocument';
import getHashParameters, { getHashParameterFromHost } from 'helpers/getHashParameters';
import fireEvent, { getEventHandler } from 'helpers/fireEvent';
import { prepareMultiTab } from 'helpers/TabManager';
import hotkeysManager from 'helpers/hotkeysManager';
import setDefaultDisabledElements from 'helpers/setDefaultDisabledElements';
import { getInstanceNode } from 'helpers/getRootNode';
import { isMobileDevice } from 'helpers/device';

import Events from 'constants/events';
import overlays from 'constants/overlays';
import { panelNames } from 'constants/panel';
import DataElements from 'constants/dataElement';
import { defaultPanels } from '../../redux/modularComponents';
import { defaultOfficeEditorPanels } from '../../redux/officeEditorModularComponents';

import setLanguage from 'src/apis/setLanguage';
import { loadDefaultFonts } from 'src/helpers/loadFont';
import './App.scss';
import LayersPanel from 'components/LayersPanel';
import TabsHeader from 'components/TabsHeader';
import useTabFocus from 'hooks/useTabFocus';
import useCloseOnWindowResize from 'hooks/useCloseOnWindowResize';
import PageManipulationFlyout from 'components/ModularComponents/PageManipulationFlyout';
import { VIEWER_CONFIGURATIONS } from 'src/constants/customizationVariables';
import useWidgetHighlightingSync from 'hooks/useWidgetHighlightingSync';
import { useTranslation } from 'react-i18next';
import { onOfficeEditorCommentAdded } from 'src/event-listeners';

// TODO: Use constants
const tabletBreakpoint = window.matchMedia('(min-width: 641px) and (max-width: 900px)');

const propTypes = {
  removeEventHandlers: PropTypes.func.isRequired,
  initialDirection: PropTypes.oneOf(['ltr','rtl']),
  instanceRootNode: PropTypes.object,
};

const App = ({ removeEventHandlers, initialDirection, instanceRootNode }) => {
  const { core } = useCore();
  const store = useStore();
  const dispatch = useDispatch();
  let timeoutReturn;

  const isInDesktopOnlyMode = useSelector(selectors.isInDesktopOnlyMode);
  const isMultiViewerMode = useSelector(selectors.isMultiViewerMode);
  const activeDocumentViewerKey = useSelector(selectors.getActiveDocumentViewerKey);
  const genericPanels = useSelector(selectors.getGenericPanels, shallowEqual);
  const customModals = useSelector(selectors.getCustomModals, shallowEqual);
  const isOfficeEditorMode = useSelector(selectors.getIsOfficeEditorMode);
  const isAccessibleMode = useSelector(selectors.isAccessibleMode);
  const activeFlyout = useSelector(selectors.getActiveFlyout);
  const currentUIConfiguration = useSelector(selectors.getUIConfiguration);
  const { i18n: instanceI18n } = useTranslation();
  const isSpreadsheetEditorModeEnabled = currentUIConfiguration === VIEWER_CONFIGURATIONS.SPREADSHEET_EDITOR;
  const defaultDirection = isSpreadsheetEditorModeEnabled ? 'ltr' : (initialDirection ?? instanceI18n?.dir?.() ?? 'ltr');
  const [direction, setDirection] = useState(defaultDirection);

  // These hooks control behaviours regarding the opening and closing of panels and in the case
  // of the redaction hook it creates a reference that tracks the redaction annotations
  useOnAnnotationCreateRubberStampToolMode();
  useOnAnnotationCreateSignatureToolMode();
  useCloseOnWindowResize(() => {
    activeFlyout && dispatch(actions.closeElements([activeFlyout]));
  });
  useTabFocus(isAccessibleMode);
  const { redactionAnnotationsList } = useOnRedactionAnnotationChanged();
  const { annotation: widgetAnnotationAddedOrSelected } = useOnFormFieldAnnotationAddedOrSelected();


  useWidgetHighlightingSync();

  useEffect(() => {
    const initialMode = getHashParameters('initialMode', null);
    const uiConfigPath = getHashParameters('uiConfig', '');
    const isOfficeEditingEnabled = getHashParameters('enableOfficeEditing', false);

    if (isOfficeEditingEnabled && isMobileDevice) {
      dispatch(actions.showWarningMessage({
        message: 'officeEditor.notSupportedOnMobile',
      }));
    }

    // Determine final mode (Explicit initialMode takes precedence, fallback to DOCX if office editing is enabled)
    let finalMode = initialMode || (isOfficeEditingEnabled ? VIEWER_CONFIGURATIONS.DOCX_EDITOR : null);

    const validModes = new Set([
      VIEWER_CONFIGURATIONS.DOCX_EDITOR,
      VIEWER_CONFIGURATIONS.SPREADSHEET_EDITOR,
      VIEWER_CONFIGURATIONS.DEFAULT,
    ]);

    if (!validModes.has(finalMode)) {
      finalMode = VIEWER_CONFIGURATIONS.DEFAULT;
    }

    // If no mode or office editing, exit early, the default UI will be loaded
    if (!finalMode) {
      return;
    }

    // Ensure default UI is loaded only if no custom `uiConfig` is set
    if (!uiConfigPath) {
      if (finalMode === VIEWER_CONFIGURATIONS.SPREADSHEET_EDITOR) {
        loadSpreadsheetEditorUI();
      } else if (finalMode === VIEWER_CONFIGURATIONS.DOCX_EDITOR) {
        loadDocxEditorUI();
      }
    }

    dispatch(actions.setUIConfiguration(finalMode));

    if (isAccessibleMode) {
      if (finalMode === VIEWER_CONFIGURATIONS.DEFAULT) {
        dispatch(actions.setShouldAddA11yContentToDOM(true));
      } else {
        console.warn('Accessibility mode is not compatible with this mode:', finalMode);
        dispatch(actions.setAccessibleMode(false));
      }
    }
  }, []);

  const loadSpreadsheetEditorUI = () => {
    dispatch(actions.restoreComponents(VIEWER_CONFIGURATIONS.SPREADSHEET_EDITOR));
    dispatch(actions.enableSpreadsheetEditorMode());
  };

  const loadDocxEditorUI = () => {
    dispatch(actions.restoreComponents(VIEWER_CONFIGURATIONS.DOCX_EDITOR));
  };

  useEffect(() => {
    loadDefaultFonts();
    const isOfficeEditingEnabled = getHashParameters('enableOfficeEditing', false);
    if (genericPanels.length === 0) {
      if (isOfficeEditingEnabled) {
        dispatch(actions.setGenericPanels(defaultOfficeEditorPanels));
      } else {
        dispatch(actions.setGenericPanels(defaultPanels));
      }
    }
  }, []);

  useEffect(() => {
    // Capture the WC host element ONCE at mount (preferring the per-instance root from renderInstanceApp, falling back to the singleton for iframe/legacy), since the module-level getInstanceNode() singleton flips as each new instance mounts and would otherwise leak another viewer's attributes into async callers here.
    const wcHost = window.isApryseWebViewerWebComponent
      ? (instanceRootNode?.host || getInstanceNode())
      : null;
    const readHashParam = wcHost
      ? (param, defaultValue) => getHashParameterFromHost(wcHost, param, defaultValue)
      : getHashParameters;

    // To avoid race condition with window.dispatchEvent firing before window.addEventListener
    setTimeout(() => {
      fireEvent(Events.VIEWER_LOADED);
    }, 300);
    window.isApryseWebViewerWebComponent ?
      fireEvent('ready', undefined, wcHost) :
      window.parent.postMessage(
        {
          type: 'viewerLoaded',
          id: parseInt(readHashParam('id'), 10),
        },
        '*',
      );

    async function loadInitialDocument() {
      let initialDoc = readHashParam('d', '');

      let defaultFile = null;
      if (!initialDoc) {
        defaultFile = getDefaultFile();
      }

      if (defaultFile) {
        loadDocument(dispatch, null, {
          filename: defaultFile,
          isOfficeEditingEnabled: true,
        }, activeDocumentViewerKey);

        return;
      }

      const state = store.getState();
      const doesAutoLoad = readHashParam('auto_load', true);
      initialDoc = initialDoc ? JSON.parse(initialDoc) : '';
      initialDoc = Array.isArray(initialDoc) ? initialDoc : [initialDoc];
      const isMultiTabAlreadyEnabled = state.viewer.isMultiTab;
      const isMultiDoc = initialDoc.length > 1;
      const startOffline = readHashParam('startOffline', false);
      const basePath = readHashParam('basePath', '');
      window.Core.setBasePath(basePath);

      if (isMultiDoc && !isMultiTabAlreadyEnabled) {
        prepareMultiTab(initialDoc, store);
        initialDoc = initialDoc[0];
        if ((initialDoc && doesAutoLoad) || startOffline) {
          const options = {
            externalPath: readHashParam('p', ''),
            documentId: readHashParam('did', null),
          };
          loadDocument(dispatch, initialDoc, options, activeDocumentViewerKey);
        }
      } else {
        const activeTab = state.viewer.activeTab || 0;
        initialDoc = initialDoc[activeTab];
        if ((initialDoc && doesAutoLoad) || startOffline) {
          let chunkSize = readHashParam('chunkSize', undefined);
          try {
            chunkSize = chunkSize ? parseInt(chunkSize) : undefined;
          } catch (e) {
            console.error('chunkSize must be a number');
          }
          const options = {
            extension: readHashParam('extension', null),
            filename: readHashParam('filename', null),
            externalPath: readHashParam('p', ''),
            documentId: readHashParam('did', null),
            showInvalidBookmarks: readHashParam('showInvalidBookmarks', false),
            chunkSize,
            spreadsheetEditorOptions: (() => {
              const optionsStr = readHashParam('spreadsheetEditorOptions', null);
              return optionsStr ? JSON.parse(optionsStr) : {};
            })(),
          };
          loadDocument(dispatch, initialDoc, options, activeDocumentViewerKey);
        }
      }
    }

    function getDefaultFile() {
      const initialMode = readHashParam('initialMode', null);
      const isOfficeEditingEnabled = readHashParam('enableOfficeEditing', false);

      if (initialMode === VIEWER_CONFIGURATIONS.DOCX_EDITOR || isOfficeEditingEnabled) {
        return 'Untitled.docx';
      }
      if (initialMode === VIEWER_CONFIGURATIONS.SPREADSHEET_EDITOR) {
        return 'Untitled.xlsx';
      }
      return null;
    }

    let initialLoadFired = false;
    function loadDocumentAndCleanup() {
      // Idempotent: whichever of the postMessage handler, 500ms fallback, or
      // explicit wcElement.loadInitialDocument() call fires first wins.
      // Subsequent calls are no-ops so we never double-load.
      if (initialLoadFired) {
        return;
      }
      initialLoadFired = true;
      loadInitialDocument();
      window.removeEventListener('message', messageHandler);
      clearTimeout(timeoutReturn);
    }

    function messageHandler(event) {
      if (event.isTrusted && typeof event.data === 'object' && event.data.type === 'viewerLoaded') {
        loadDocumentAndCleanup();
      }
    }

    window.addEventListener('blur', () => {
      dispatch(actions.closeElements(overlays));
    });
    window.addEventListener('message', messageHandler, false);

    // When a user switches tabs, the focused element can cause unexpected behavior,
    // such as triggering keyboard events or displaying tooltips, when they return
    // to the tab. Blurring the focused element before switching tabs helps to
    // prevent these unwanted interactions.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        if (document.activeElement) {
          document.activeElement.blur();
        }
      }
    });

    // In WebComponent mode there is no postMessage handshake, so expose an
    // explicit trigger on the WC host element so a host (test harness or
    // sample) can deterministically start the initial document load AFTER
    // it has registered its event listeners. The 500ms timeout below is
    // kept as a fallback for hosts that don't call this method (preserves
    // existing auto-load behavior). Calling the method first cancels the
    // timer via loadDocumentAndCleanup.
    if (window.isApryseWebViewerWebComponent) {
      if (wcHost && typeof wcHost === 'object') {
        wcHost.loadInitialDocument = loadDocumentAndCleanup;
      }
    }

    // In case WV is used outside of iframe, postMessage will not
    // receive the message, and this timeout will trigger loadInitialDocument
    timeoutReturn = setTimeout(loadDocumentAndCleanup, 500);

    return removeEventHandlers;
  }, []);

  useEffect(() => {
    const setTabletState = () => {
      // TODO: Use constants
      dispatch(actions.setLeftPanelWidth(251));
      dispatch(actions.setNotesPanelWidth(293));
      dispatch(actions.setSearchPanelWidth(293));
    };

    const onBreakpoint = () => {
      if (tabletBreakpoint.matches) {
        setTabletState();
      }
    };
    tabletBreakpoint.addListener(onBreakpoint);
  }, []);

  useEffect(() => {
    const onLanguageChange = () => {
      if (isSpreadsheetEditorModeEnabled) {
        setDirection('ltr');
        return;
      }
      setDirection(instanceI18n?.dir?.() ?? 'ltr');
    };
    instanceI18n.on('languageChanged', onLanguageChange);

    // Only sync Redux → i18n if we're *not* in an overridden RTL mode
    if (initialDirection !== 'rtl') {
      setLanguage(store, instanceI18n)(store.getState().viewer.currentLanguage);
    }

    return () => {
      instanceI18n.off('languageChanged', onLanguageChange);
    };
  }, [currentUIConfiguration]);

  // These need to be done only once on app load
  useEffect(() => {
    hotkeysManager.initialize(store);
    setDefaultDisabledElements(store);
  }, []);

  useEffect(() => {
    const onError = (error) => {
      // The LOAD_ERROR event always represents a load error.
      let errorTitle = 'message.loadError';
      error = error?.message ?? error;

      let errorMessage;

      if (typeof error === 'string') {
        errorMessage = error;

        // provide a more specific error message
        if (errorMessage.includes('File does not exist')) {
          errorMessage = 'message.notSupported';
        }
      } else if (error?.type === 'InvalidPDF') {
        errorMessage = 'message.badDocument';
      }

      if (errorMessage) {
        dispatch(actions.showErrorMessage(errorMessage, errorTitle));
      }
    };

    getEventHandler().addEventListener(Events.LOAD_ERROR, onError);
    return () => getEventHandler().removeEventListener(Events.LOAD_ERROR, onError);
  }, []);

  useEffect(() => {
    // update cursor and selection properties for Office Editor custom UI
    if (isOfficeEditorMode) {
      const onCursorPropertiesUpdated = async (cursorProperties) => {
        dispatch(actions.setOfficeEditorCursorProperties(cursorProperties));
      };
      const onSelectionPropertiesUpdated = (selectionProperties) => {
        dispatch(actions.setOfficeEditorSelectionProperties(selectionProperties));
      };
      const onOfficeEditorUndoRedoStateChanged = (canUndo, canRedo) => {
        dispatch(actions.setOfficeEditorCanUndo(canUndo));
        dispatch(actions.setOfficeEditorCanRedo(canRedo));
      };
      const onOfficeEditorCommentAddedHandler = onOfficeEditorCommentAdded(store, core);

      core.getDocument().addEventListener('cursorPropertiesUpdated', onCursorPropertiesUpdated);
      core.getDocument().addEventListener('selectionPropertiesUpdated', onSelectionPropertiesUpdated);
      core.getDocument().addEventListener('officeEditorUndoRedoStateChanged', onOfficeEditorUndoRedoStateChanged);
      core.getDocument().addEventListener('commentThreadAddedDebounced', onOfficeEditorCommentAddedHandler);

      return () => {
        core.getDocument().removeEventListener('selectionPropertiesUpdated', onSelectionPropertiesUpdated);
        core.getDocument().removeEventListener('cursorPropertiesUpdated', onCursorPropertiesUpdated);
        core.getDocument().removeEventListener('officeEditorUndoRedoStateChanged', onOfficeEditorUndoRedoStateChanged);
        core.getDocument().removeEventListener('commentThreadAddedDebounced', onOfficeEditorCommentAddedHandler);
      };
    }
  }, [isOfficeEditorMode]);

  const renderPanel = (panelName, dataElement) => {
    switch (panelName) {
      case panelNames.OUTLINE:
        return <LazyLoadWrapper Component={LazyLoadComponents.OutlinesPanel} dataElement={dataElement} />;
      case panelNames.SIGNATURE:
        return <LazyLoadWrapper Component={LazyLoadComponents.SignaturePanel} dataElement={dataElement} />;
      case panelNames.BOOKMARKS:
        return <LazyLoadWrapper Component={LazyLoadComponents.BookmarksPanel} dataElement={dataElement} />;
      case panelNames.FILE_ATTACHMENT:
        return <LazyLoadWrapper Component={LazyLoadComponents.FileAttachmentPanel} dataElement={dataElement} />;
      case panelNames.THUMBNAIL:
        return <LazyLoadWrapper Component={LazyLoadComponents.ThumbnailsPanel} dataElement={dataElement} />;
      case panelNames.LAYERS:
        return <LayersPanel dataElement={dataElement} />;
      case panelNames.TEXT_EDITING:
        return <TextEditingPanel dataElement={dataElement} />;
      case panelNames.CHANGE_LIST:
        return <ComparePanel dataElement={dataElement}/>;
      case panelNames.STYLE:
        return <LazyLoadWrapper Component={LazyLoadComponents.StylePanel} dataElement={dataElement} />;
      case panelNames.REDACTION:
        return <LazyLoadWrapper Component={LazyLoadComponents.RedactionPanel} dataElement={dataElement} redactionAnnotationsList={redactionAnnotationsList} isCustomPanel={true} />;
      case panelNames.SEARCH:
        return <LazyLoadWrapper Component={LazyLoadComponents.SearchPanel} dataElement={dataElement} />;
      case panelNames.NOTES:
        return <LazyLoadWrapper Component={LazyLoadComponents.NotesPanel} dataElement={dataElement} isCustomPanel={true} />;
      case panelNames.INDEX:
        return <LazyLoadWrapper Component={LazyLoadComponents.IndexPanel} dataElement={dataElement} />;
      case panelNames.TABS:
        return <LazyLoadWrapper Component={LazyLoadComponents.TabPanel} dataElement={dataElement} redactionAnnotationsList={redactionAnnotationsList} />;
      case panelNames.SIGNATURE_LIST:
        return <LazyLoadWrapper Component={LazyLoadComponents.SignatureListPanel} dataElement={dataElement} />;
      case panelNames.RUBBER_STAMP:
        return <LazyLoadWrapper Component={LazyLoadComponents.RubberStampPanel} dataElement={dataElement} />;
      case panelNames.PORTFOLIO:
        return <LazyLoadWrapper Component={LazyLoadComponents.PortfolioPanel} dataElement={dataElement} />;
      case panelNames.FORM_FIELD:
        return <LazyLoadWrapper Component={LazyLoadComponents.FormFieldPanel} dataElement={dataElement} annotation={widgetAnnotationAddedOrSelected} />;
    }
  };

  const panels = genericPanels.map((panel, index) => {
    return (
      panel.render && (
        <Panel key={index} dataElement={panel.dataElement} location={panel.location} isCustom={true}>
          {Object.values(panelNames).includes(panel.render) ? renderPanel(panel.render, panel.dataElement) : (
            <CustomElement
              key={panel.dataElement || index}
              className={`Panel ${panel.dataElement}`}
              display={panel.dataElement}
              dataElement={panel.dataElement}
              render={panel.render}
              isCustomPanel={true}
            />
          )}
        </Panel>
      )
    );
  });

  return (
    <>
      <div
        className={classNames({
          'App': true,
          'is-in-desktop-only-mode': isInDesktopOnlyMode,
          'is-web-component': window.isApryseWebViewerWebComponent,
        })} dir={direction}
      >
        <FlyoutContainer />
        <RibbonOverflowFlyout />
        <PageManipulationFlyout />
        <StylePanelFlyout />
        <Accessibility />
        <TabsHeader />
        <TopHeader />
        {isSpreadsheetEditorModeEnabled &&
          <LazyLoadWrapper Component={LazyLoadComponents.FormulaBar} dataElement={DataElements.FORMULA_BAR}/>}
        <div className="content">
          <LeftHeader/>
          {panels}
          {window?.ResizeObserver && <MultiViewer />}
          <MultiTabEmptyPage />
          <RightHeader/>
          <BottomHeader/>
          {!isMultiViewerMode && <DocumentContainer />}
        </div>
        <LazyLoadWrapper
          Component={LazyLoadComponents.ViewControlsOverlay}
          dataElement={DataElements.VIEW_CONTROLS_OVERLAY}
        />
        <LazyLoadWrapper
          Component={LazyLoadComponents.MenuOverlay}
          dataElement={DataElements.MENU_OVERLAY}
        />
        <LazyLoadWrapper
          Component={LazyLoadComponents.ZoomOverlay}
          dataElement={DataElements.ZOOM_OVERLAY}
        />
        <LazyLoadWrapper
          Component={LazyLoadComponents.AnnotationContentOverlay}
          dataElement={DataElements.ANNOTATION_CONTENT_OVERLAY}
          onOpenHook={useOnAnnotationContentOverlayOpen}
        />
        <LazyLoadWrapper
          Component={LazyLoadComponents.LinkAnnotationPopup}
          dataElement={DataElements.LINK_ANNOTATION_POPUP}
          onOpenHook={useOnLinkAnnotationPopupOpen}
        />
        <LazyLoadWrapper
          Component={LazyLoadComponents.PageManipulationOverlay}
          dataElement={DataElements.PAGE_MANIPULATION_OVERLAY}
        />
        <LazyLoadWrapper
          Component={LazyLoadComponents.ThumbnailMoreOptionsPopup}
          dataElement={DataElements.THUMBNAILS_CONTROL_MANIPULATE_POPUP}
        />
        <LazyLoadWrapper
          Component={LazyLoadComponents.ThumbnailMoreOptionsPopupSmall}
          dataElement={DataElements.THUMBNAILS_CONTROL_MANIPULATE_POPUP_SMALL}
        />
        <FormFieldIndicatorContainer />
        {/* Popups */}
        {/* AnnotationPopup should be the first so that other popups can lay on top of it */}
        <LazyLoadWrapper
          Component={LazyLoadComponents.AnnotationPopup}
          dataElement={DataElements.ANNOTATION_POPUP}
          onOpenHook={useOnAnnotationPopupOpen}
        />
        <LazyLoadWrapper
          Component={LazyLoadComponents.AlignmentPopup}
          dataElement={DataElements.ANNOTATION_ALIGNMENT_POPUP}
          onOpenHook={useOnAlignmentPopupOpen}
        />
        <LazyLoadWrapper
          Component={LazyLoadComponents.TextPopup}
          dataElement={DataElements.TEXT_POPUP}
          onOpenHook={useOnTextSelected}
        />
        <LazyLoadWrapper
          Component={LazyLoadComponents.ContextMenuPopup}
          dataElement={DataElements.CONTEXT_MENU_POPUP}
          onOpenHook={useOnContextMenuOpen}
        />
        <LazyLoadWrapper
          Component={LazyLoadComponents.InlineCommentingPopup}
          dataElement={DataElements.INLINE_COMMENT_POPUP}
          onOpenHook={useOnInlineCommentPopupOpen}
        />
        <AudioPlaybackPopup />
        <DocumentCropPopup />
        <SnippingToolPopup />
        {/* Modals */}
        <LazyLoadWrapper
          Component={LazyLoadComponents.ScaleModal}
          dataElement={DataElements.SCALE_MODAL}
          onOpenHook={useOnMeasurementToolOrAnnotationSelected}
        />
        <LazyLoadWrapper
          Component={LazyLoadComponents.ScaleOverlayContainer}
          dataElement={DataElements.SCALE_OVERLAY_CONTAINER}
          onOpenHook={useOnMeasurementToolOrAnnotationSelected}
        />
        <LazyLoadWrapper
          Component={LazyLoadComponents.MeasurementOverlay}
          dataElement={DataElements.MEASUREMENT_OVERLAY}
          onOpenHook={useOnCountMeasurementAnnotationSelected}
        />
        <LazyLoadWrapper Component={LazyLoadComponents.ContentEditLinkModal} dataElement={DataElements.CONTENT_EDIT_LINK_MODAL} />
        <LazyLoadWrapper Component={LazyLoadComponents.SignatureModal} dataElement={DataElements.SIGNATURE_MODAL} />
        <LazyLoadWrapper Component={LazyLoadComponents.PrintModal} dataElement={DataElements.PRINT_MODAL} />
        <LazyLoadWrapper Component={LazyLoadComponents.ErrorModal} dataElement={DataElements.ERROR_MODAL} />
        <LazyLoadWrapper Component={LazyLoadComponents.PasswordModal} dataElement={DataElements.PASSWORD_MODAL} />
        <LazyLoadWrapper
          Component={LazyLoadComponents.CreateStampModal}
          dataElement={DataElements.CUSTOM_STAMP_MODAL}
        />
        <LazyLoadWrapper
          Component={LazyLoadComponents.PageReplacementModal}
          dataElement={DataElements.PAGE_REPLACEMENT_MODAL}
        />
        <LazyLoadWrapper
          Component={LazyLoadComponents.LinkModal}
          dataElement={DataElements.LINK_MODAL}
          onOpenHook={useOnRightClickAnnotation}
        />
        <LazyLoadWrapper Component={LazyLoadComponents.FilterAnnotModal} dataElement={DataElements.FILTER_MODAL} />
        <LazyLoadWrapper
          Component={LazyLoadComponents.PageRedactionModal}
          dataElement={DataElements.PAGE_REDACT_MODAL}
        />
        <LazyLoadWrapper Component={LazyLoadComponents.CalibrationModal} dataElement={DataElements.CALIBRATION_MODAL} />
        <LazyLoadWrapper Component={LazyLoadComponents.SettingsModal} dataElement={DataElements.SETTINGS_MODAL} />
        <LazyLoadWrapper Component={LazyLoadComponents.SaveModal} dataElement={DataElements.SAVE_MODAL} />
        <LazyLoadWrapper Component={LazyLoadComponents.InsertPageModal} dataElement={DataElements.INSERT_PAGE_MODAL} />
        <LazyLoadWrapper Component={LazyLoadComponents.LoadingModal} dataElement={DataElements.LOADING_MODAL} />

        {
          /*
            There were issues appearing in WebViewer BIM add-on with lazy loading ProgressModal.
            The BIM add-on relies on ProgressModal styling which wouldn't not get loaded explicitly.
            This caused styling issues when loading a 3D model and would impact the UI of the BIM add-on.

            See https://apryse.atlassian.net/browse/WVR-3094
          */
        }
        <ProgressModal />

        <LazyLoadWrapper Component={LazyLoadComponents.WarningModal} dataElement={DataElements.WARNING_MODAL} />
        <LazyLoadWrapper
          Component={LazyLoadComponents.ColorPickerModal}
          dataElement={DataElements.COLOR_PICKER_MODAL}
        />
        <LazyLoadWrapper Component={LazyLoadComponents.OpenFileModal} dataElement={DataElements.OPEN_FILE_MODAL} />
        {customModals.length > 0 && (
          customModals.map((modal) => (
            (<LazyLoadWrapper key={modal.dataElement} Component={LazyLoadComponents.CustomModal} dataElement={modal.dataElement}/>)
          ))
        )}
        {core.isFullPDFEnabled() && (
          <LazyLoadWrapper
            Component={LazyLoadComponents.SignatureValidationModal}
            dataElement={DataElements.SIGNATURE_VALIDATION_MODAL}
          />
        )}
        <LogoBar />
        <LazyLoadWrapper Component={LazyLoadComponents.CreatePortfolioModal} dataElement={DataElements.CREATE_PORTFOLIO_MODAL} />
        <EmbeddedJSPopup />
        {isOfficeEditorMode && (
          <>
            <LazyLoadWrapper
              Component={LazyLoadComponents.HeaderFooterControlsOverlay}
              dataElement={DataElements.HEADER_FOOTER_CONTROLS_OVERLAY}
              onOpenHook={useOnHeaderFooterUpdate}/>
            <LazyLoadWrapper
              Component={LazyLoadComponents.HeaderFooterOptionsModal}
              dataElement={DataElements.HEADER_FOOTER_OPTIONS_MODAL}
            />
            <LazyLoadWrapper
              Component={LazyLoadComponents.OfficeEditorMarginsModal}
              dataElement={DataElements.OFFICE_EDITOR_MARGINS_MODAL}
            />
            <LazyLoadWrapper
              Component={LazyLoadComponents.OfficeEditorColumnsModal}
              dataElement={DataElements.OFFICE_EDITOR_COLUMNS_MODAL}
            />
          </>
        )}
      </div>

      <PrintHandler />
      <FilePickerHandler />
      <CopyTextHandler />
      <FontHandler />
    </>
  );
};

App.propTypes = propTypes;

export default hot(App);
